import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";

import { useReadinessTools } from "./readiness-tools";

const StatusPill = ({ connected }: { connected: boolean }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs ${
      connected
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-gray-200 bg-white text-gray-500"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500" : "bg-gray-300"}`}
    />
    {connected ? "Connected" : "Not connected"}
  </span>
);

/**
 * Which work tools the organization has connected, what each adds to the
 * reviews, and where to connect the rest. One row per category, with Connect
 * beside the ones still missing for people who can connect them.
 */
const ReadinessIntegrations = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const label = useLabel();
  const { categories, canManage } = useReadinessTools(organizationId);
  const integrationsUrl = `/settings/organization/${organizationId}/integrations`;

  return (
    <div>
      <ul className="divide-y">
        {categories.map((category) => (
          <li
            key={category.label}
            className="flex items-center justify-between gap-4 py-3"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{category.label}</span>
                {/* The logos say which tools the category means, with the name
                    on hover, instead of a line of names under it. */}
                {/* Each logo in a small white circle with a gray border, overlapping,
                    with a white ring so each one stays distinct. */}
                <span className="flex items-center -space-x-1.5">
                  {category.tools.map((tool) => (
                    <span
                      key={tool.name}
                      title={tool.name}
                      className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white ring-2 ring-white"
                    >
                      <img
                        src={tool.logo}
                        alt={tool.name}
                        className={`h-3.5 w-3.5 ${tool.logoIsWordmark ? "object-cover object-left" : "object-contain"}`}
                      />
                    </span>
                  ))}
                </span>
                {/* A Connect button already says a category is missing, so
                    the tag is only for the rows that have none. */}
                {(category.connected || !canManage) && (
                  <StatusPill connected={category.connected} />
                )}
              </div>
              <div className="mt-0.5 text-sm text-gray-600">
                {category.what}
              </div>
            </div>
            {canManage && !category.connected && (
              <Button
                theme={buttonTheme.lightBlue}
                size={ButtonSize.medium}
                text="Connect"
                to={integrationsUrl}
              />
            )}
          </li>
        ))}
      </ul>
      {/* aggregate_context (chatbot/context_data.py) shares work tools and
          Slack only with the person and their managers; meetings go to anyone
          who can see them. */}
      <div className="mt-3 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700">
        Generate Context draws on these tools in self and manager{" "}
        {label("review", { pluralize: true })}. Peer reviewers get meeting notes
        only.
        {!canManage &&
          " An admin connects integrations on the Integrations settings page."}
      </div>
    </div>
  );
};

export default ReadinessIntegrations;
